import * as React from "react";
import * as yup from "yup";

import { ITerminologyItem } from "src/library/utils/string-manipulations/tokenize-string";
import { Text } from "src/library/components/Text";
import {
  InheritedSettingNameType,
  IInheritedSetting,
  OrderedInheritedSettings
} from "src/core/drops/api/drops.api";

import DefaultCutoff from "./Inherited-settings/DefaultCutoff";
import { LeadTimeSetting } from "./Inherited-settings/LeadTimesSetting";

type LeadTimeSettingNames = Extract<InheritedSettingNameType, | "delivery_cutoff" | "delivery_week_leadtimes">;

export type IFormFieldValues = { [P in LeadTimeSettingNames]: IFormValue } & {
  delivery_week_leadtimes?: IFormValue;
  delivery_cutoff?: IFormValue;
};

export interface IFormValue {
  name: LeadTimeSettingNames;
  inherited: boolean;
  inheritedValue?: IInheritedSetting<any>["value"];
  value?: IInheritedSetting<any>["value"];
  label: string | number;
  Description?: JSX.Element;
  hidden?: boolean;
  SettingElement: (setFieldValue: any, setting: any, handleBlur: any, disabled?: boolean, formValues?: IFormFieldValues) => React.ReactNode;
}

/**
 * This hook is used to render the form components in a way that respects the hierachy rules of inherited settings
 * for default cutoff and delivery week lead times
 */
const useCascadeLeadTimeSettings = (
  scope: ITerminologyItem,
  inheritedValues?: OrderedInheritedSettings<IInheritedSetting<any>>,
  overrideValues?: OrderedInheritedSettings<IInheritedSetting<any>>
): [IFormFieldValues | null] => {
  // On getDeliveryDaysResponse change
  const settings = React.useMemo(() => {
    // Set the settings state to null if there is no values to base logic off of
    if (!(overrideValues && inheritedValues && inheritedValues.delivery_cutoff)) {
      return null;
    }

    // Update the settings state
    const finalSettings: IFormFieldValues = {
      delivery_cutoff: {
        name: "delivery_cutoff",
        inherited: overrideValues && overrideValues.delivery_cutoff?.value ? false : true,
        inheritedValue: inheritedValues?.delivery_cutoff?.value?.time,
        label: inheritedValues?.delivery_cutoff?.value.time,
        value: overrideValues?.delivery_cutoff?.value ?? inheritedValues?.delivery_cutoff?.value ?? { time: "00:00" },
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              Default Cutoff
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets up the default cutoff time for this ${scope.singular.text}.`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, _handleBlur, disabled) => {
          if (!setting.value) {
            return null;
          }

          return (
            <DefaultCutoff
              disabled={!!disabled}
              value={setting.value.time}

              handleChange={time => {
                setFieldValue(setting.name, {
                  ...setting,
                  inherited: false,
                  value: { time }
                });
              }}
            />
          );
        }
      },
      delivery_week_leadtimes: {
        name: "delivery_week_leadtimes",
        inherited: overrideValues && overrideValues.delivery_week_leadtimes ? false : true,
        label: inheritedValues?.delivery_week_leadtimes?.scope || "",
        inheritedValue: inheritedValues?.delivery_week_leadtimes?.value,
        value: overrideValues?.delivery_week_leadtimes?.value ?? inheritedValues?.delivery_week_leadtimes?.value,
        Description: (
          <>
            <Text
              as="h3"
              fontSize="h3"
            >
              {`${scope.singular.text} lead time settings`}
            </Text>

            <Text
              as="p"
              fontSize="p"
            >
              {`This sets the lead time configuration for this ${scope.singular.text}`}
            </Text>
          </>
        ),
        SettingElement: (setFieldValue, setting, _handleBlur, disabled, values) => {
          const currentCutoff = values?.delivery_cutoff.value;

          if (!currentCutoff) {
            return null;
          }

          return (
            <LeadTimeSetting
              currentCutoff={currentCutoff.time}
              disabled={!!disabled}
              value={setting.value}
              handleChange={newLeadTimes => setFieldValue(setting.name, {
                ...setting,
                inherited: false,
                value: newLeadTimes
              })}
            />
          );
        }
      }
    };

    return finalSettings || null;
  }, [
    inheritedValues,
    overrideValues,
    scope.singular
  ]);

  return [ settings ];
};

export default useCascadeLeadTimeSettings;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const cascadeSettingsSchema = () =>
  yup
    .object()
    .shape({
      delivery_cutoff: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Default Cutoff")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .shape({
                time: yup
                  .string()
                  .typeError("Default Cutoff must be a time")
                  .label("Default Cutoff")
                  .required()
              })
              .required()
          })
          .nullable()
      }),
      delivery_week_leadtimes: yup.object().shape({
        inherited: yup.boolean().required(),
        value: yup
          .object()
          .label("Lead time settings")
          .when("inherited", {
            is: false,
            then: yup
              .object()
              .required()
          })
          .nullable()
      })

    })
    .required();
